import FormGroup from "@material-ui/core/FormGroup";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Controller, UseFormMethods, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { emailMustMatch, emailRegex, noSpacesRegex, OTPemailRegex, OTPnoSpacesRegex } from "@utils/formControls";
import { ICustomerDetailsForm } from "./CustomerDetailsForm";
import { useFetch } from "@common/hooks/useFetch";
import { useEffect, useState } from "react";
import { IBooleanResponse } from "@common/IBooleanResponse";
import { checkIfEmailExists, sendOtp } from "@api-routes";
import { Button } from "@material-ui/core";
import { getLanguage } from "@utils/changeLanguage";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { IError } from "src/itsf/fetch/fetcherUtils";

interface IParams {
    emailError?: string;
    formMethods: UseFormMethods<ICustomerDetailsForm>;
    pathName: string;
    setEmailVerified(isVerified: boolean): void;
    emailVerified: boolean;
}

export const CustomerDetailsEmail = (props: IParams) => {
    const { pathName, formMethods, emailError, setEmailVerified, emailVerified } = props;
    const { t } = useTranslation(["customerDetails", "validation", "portin"]);
    const [showEmailOtpError, setShowEmailOtpError] = useState<boolean>(false);
    const [showEmailExistsError, setShowEmailExistsError] = useState<boolean>(false);
    const [showEmailNotAcceptedError, setShowEmailNotAcceptedError] = useState<boolean>(false);
    const [disableSendOTP, setDisableSendOTP] = useState<boolean>(true);
    const [OTP, setOTP] = useState<string>("");
    const [_, { post }] = useFetch();
    const verifyEmailTranslated: string = t("customerDetails:verifyEmail");
    const verifyOTPTranslated: string = t("customerDetails:verifyOTP");
    const [verifyEmailText, setVerifyEmailText] = useState<string>(verifyEmailTranslated);
    const emailConfirm = useWatch<string>({ name: "emailConfirm", control: formMethods.control });
    const email = useWatch<string>({ name: "email", control: formMethods.control });
    const isAuthenticated = keycloakUtils.isAuthenticated();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const logIn = () => {
        keycloakUtils.login({
            redirectUri: `${window.location.origin}/#${pathName}?`,
        });
    };

    useEffect(() => {
        if (
            OTPemailRegex(email || "") &&
            OTPnoSpacesRegex(email || "") &&
            OTPemailRegex(emailConfirm || "") &&
            OTPnoSpacesRegex(emailConfirm || "") &&
            email === emailConfirm
        ) {
            setDisableSendOTP(false);
        } else {
            setDisableSendOTP(true);
        }
        setVerifyEmailText(verifyEmailTranslated);
        setOTP("");
        setEmailVerified(false);
        setShowEmailOtpError(false);
        setShowEmailExistsError(false);
        setShowEmailNotAcceptedError(false);
    }, [email, emailConfirm]);

    const onVerifyEmailButtonClick = (resend: boolean) => {
        if (verifyEmailText === verifyEmailTranslated && !resend) {
            checkIfEmailAlreadyExists();
        } else if (resend) {
            setOTP("");
            setEmailVerified(false);
            setShowEmailOtpError(false);
            sendOtpFunction("EMAIL");
        } else {
            verifyOtpFunction("EMAIL");
        }
    };

    const checkIfEmailAlreadyExists = () => {
        var bodyRequest = {
            email: email,
        };

        post<IBooleanResponse>(checkIfEmailExists(), {
            body: bodyRequest,
            isSecured: keycloakUtils.isAuthenticated(),
        }).then((response) => {
            if (response && response.result) setShowEmailExistsError(true);
            else {
                setShowEmailExistsError(false);
                sendOtpFunction("EMAIL");
            }
        });
    };

    const sendOtpFunction = async (type: String) => {
        if (!executeRecaptcha) {
            console.error("sendOtpFunction email - reCAPTCHA hasn't loaded yet");
            return;
        }

        const recaptchaToken = await executeRecaptcha("sendOTP");

        const bodyRequest = {
            receiver: email,
            type: type,
            lang: getLanguage(),
            recaptchaToken: recaptchaToken,
        };

        setShowEmailNotAcceptedError(false);

        post<IBooleanResponse>(sendOtp(), {
            body: bodyRequest,
            isSecured: keycloakUtils.isAuthenticated(),
            forwardError: true,
        })
            .then((response) => {
                if (response?.result) {
                    if (type === "EMAIL") {
                        setVerifyEmailText(verifyOTPTranslated);
                    }
                }
            })
            .catch((error: IError) => {
                if (error?.status === 400) {
                    setShowEmailNotAcceptedError(true);
                }
            });
    };

    const verifyOtpFunction = (type: string) => {
        var bodyVerifyRequest = {
            receiver: email,
            otp: OTP,
            type: type,
        };

        post<IBooleanResponse>(sendOtp() + "/verify", {
            body: bodyVerifyRequest,
            isSecured: keycloakUtils.isAuthenticated(),
        }).then((response) => {
            if (response?.result) {
                if (type === "EMAIL") {
                    setEmailVerified(true);
                    setVerifyEmailText(verifyEmailTranslated);
                    setShowEmailOtpError(false);
                }
            } else {
                if (type === "EMAIL") {
                    setShowEmailOtpError(true);
                }
            }
        });
    };

    return (
        <section className="customer-form__title">
            <Grid container justify="center">
                <Grid item md={5} xs={12}>
                    <Typography color="primary" variant="h2">
                        {t("customerDetails:emailAddress")}
                    </Typography>
                </Grid>
            </Grid>
            <div>
                {!isAuthenticated && (
                    <Typography color="secondary" component="p" gutterBottom variant="body1">
                        {t("customerDetails:alreadyRegistered")}
                        <Typography
                            className="customer-form__link"
                            color="secondary"
                            component="a"
                            tabIndex={0}
                            variant="body1"
                            onClick={logIn}
                        >
                            {t("customerDetails:clickHere")}
                        </Typography>
                        {t("customerDetails:login")}
                    </Typography>
                )}
            </div>
            <FormGroup className="customer-form-fields" row={false}>
                <Grid container>
                    <Grid item md={5} xs={12}>
                        <Controller
                            control={formMethods.control}
                            name="email"
                            render={(renderProps) => (
                                <TextField
                                    aria-describedby="emailHelper"
                                    disabled={isAuthenticated}
                                    error={Boolean(formMethods.errors.email) || Boolean(emailError)}
                                    fullWidth
                                    helperText={formMethods.errors.email?.message ?? emailError}
                                    id="email"
                                    inputRef={renderProps.ref}
                                    label={`${t("customerDetails:yourEmail")} *`}
                                    margin="dense"
                                    name={renderProps.name}
                                    type="text"
                                    value={renderProps.value}
                                    onChange={renderProps.onChange}
                                />
                            )}
                            rules={{
                                required: t("validation:required"),
                                validate: (value: string) => noSpacesRegex(value) ?? emailRegex(value),
                            }}
                        />
                        {showEmailExistsError && (
                            <Typography color="error" variant="body2" style={{ textAlign: "left" }}>
                                {t("customerDetails:emailAlreadyExists")}
                            </Typography>
                        )}
                        {showEmailNotAcceptedError && (
                            <Typography color="error" variant="body2" style={{ textAlign: "left" }}>
                                {t("customerDetails:emailNotAccepted")}
                            </Typography>
                        )}
                        <FormHelperText id="emailHelper">{t("customerDetails:emailHelper")}</FormHelperText>
                    </Grid>
                    <Grid item md={2} xs={12} />
                    <Grid item md={5} xs={12}>
                        {!isAuthenticated && (
                            <Controller
                                control={formMethods.control}
                                name="emailConfirm"
                                render={(renderProps) => (
                                    <TextField
                                        aria-describedby="emailConfirmHelper"
                                        error={Boolean(formMethods.errors.emailConfirm)}
                                        fullWidth
                                        helperText={formMethods.errors.emailConfirm?.message}
                                        id="email-confirm"
                                        inputRef={renderProps.ref}
                                        label={`${t("customerDetails:confirmEmail")} *`}
                                        margin="dense"
                                        name={renderProps.name}
                                        type="text"
                                        onChange={renderProps.onChange}
                                        onPaste={(event) => event.preventDefault()}
                                    />
                                )}
                                rules={{
                                    required: t("validation:required"),
                                    validate: (value: string) =>
                                        noSpacesRegex(value) ?? emailRegex(value) ?? emailMustMatch(value, email),
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
            </FormGroup>

            <FormGroup className="customer-form-fields" style={{ textAlign: "left" }}>
                <Grid container spacing={3}>
                    {verifyEmailText === verifyOTPTranslated && (
                        <Grid item md={4} xs={12}>
                            <TextField
                                label={`${t("customerDetails:enterOTP")} *`}
                                value={OTP}
                                margin="dense"
                                fullWidth
                                name="OTP"
                                type="text"
                                onChange={(event) => {
                                    setOTP(event.target.value);
                                }}
                            />
                            <Typography color="textSecondary" gutterBottom id="emailOtpHelper" variant="body2">
                                {t("customerDetails:emailOtpHelper")}
                            </Typography>
                            {showEmailOtpError && (
                                <Typography color="error" variant="body2">
                                    {t("portin:invalidOTP")}
                                </Typography>
                            )}
                        </Grid>
                    )}
                    {!emailVerified && (
                        <Grid item>
                            <Button
                                disabled={disableSendOTP || (verifyEmailText === verifyOTPTranslated && !OTP)}
                                color="primary"
                                type="button"
                                variant="contained"
                                onClick={() => {
                                    onVerifyEmailButtonClick(false);
                                }}
                            >
                                {verifyEmailText}
                            </Button>
                        </Grid>
                    )}
                    {showEmailOtpError && (
                        <Grid item>
                            <Button
                                color="primary"
                                type="button"
                                variant="contained"
                                onClick={() => {
                                    onVerifyEmailButtonClick(true);
                                }}
                            >
                                {t("portin:resendOTP")}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </FormGroup>
        </section>
    );
};
